import { useId } from 'react'
import { Card } from '../../components/ui/card'
import { Chip } from '../../components/ui/chip'
import { useI18n } from '../lib/i18n'
import type { CompanyDetail } from '../lib/screens'

type CompanyNoa = CompanyDetail['noa']

const STATE_TONE = {
  on: 'success',
  off: 'light',
  updating: 'danger',
  paused: 'danger',
} as const satisfies Record<CompanyNoa['state'], string>

/*
 * Noa in a company's detail (ADR 0010 §8 and §9): whether it is on, off, paused for an update
 * (it runs with other versions than the ones it was validated with when the operator turned it on)
 * or paused at the company's monthly cap; a warning past 80 % of the cap; and the versions it was
 * validated with beside the running ones. Never an amount nor the cap: the operator tool gives
 * them. Not in the Figma (its Noa block is not built, ADR 0009 §8): marked for design review.
 */
export function CompanyNoaSection({ noa }: { noa: CompanyNoa }) {
  const { t } = useI18n()
  const id = useId()
  const versions = [
    ['prompt', 'company.noa.prompt'],
    ['crisisTerms', 'company.noa.crisisTerms'],
    ['model', 'company.noa.model'],
  ] as const
  return (
    <section aria-labelledby={id}>
      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <h2 id={id} className="text-lg font-medium">
            {t('company.noa.title')}
          </h2>
          <Chip tone={STATE_TONE[noa.state]}>{t(`company.noa.state.${noa.state}`)}</Chip>
        </div>
        <p className="mt-2 text-sm">{t(`company.noa.${noa.state}`)}</p>
        {noa.state === 'on' && noa.band === 'warning' && (
          <p className="mt-2 text-sm text-danger">{t('company.noa.warning')}</p>
        )}
        {noa.state !== 'off' && (
          <dl className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="min-w-0">
              <dt className="text-xs text-ink-muted">{t('company.noa.validated')}</dt>
              <dd className="mt-1 space-y-0.5 text-sm [overflow-wrap:anywhere]">
                {versions.map(([field, label]) => (
                  <p key={field}>
                    {t(label)}: <code>{noa.validated[field]}</code>
                  </p>
                ))}
                <p>
                  {t('company.noa.validatedAt')}:{' '}
                  <time dateTime={noa.validated.at}>
                    {t('company.noa.at', { at: new Date(noa.validated.at) })}
                  </time>
                </p>
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-xs text-ink-muted">{t('company.noa.running')}</dt>
              <dd className="mt-1 space-y-0.5 text-sm [overflow-wrap:anywhere]">
                {versions.map(([field, label]) => (
                  <p key={field}>
                    {t(label)}:{' '}
                    {noa.running[field] === null ? (
                      t('company.noa.unknown')
                    ) : (
                      <code>{noa.running[field]}</code>
                    )}
                  </p>
                ))}
              </dd>
            </div>
          </dl>
        )}
        <p className="mt-4 text-xs text-ink-muted">{t('company.noa.amount')}</p>
      </Card>
    </section>
  )
}
